import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LiveKitRoom,
  VideoConference,
  ControlBar,
  useTracks,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  Chat,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import {
  Loader2,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Monitor,
  MonitorOff,
  MessageSquare,
  Users,
  Settings,
  MoreVertical,
  AlertCircle,
  X,
  ArrowLeft,
  Maximize,
  Minimize,
  Hand,
  CircleDot,
} from 'lucide-react';
import { meetingsApi } from '../../../lib/api';
import { useAuth } from '../../../app/providers/AuthProvider';

interface MeetingDetails {
  id: string;
  title: string;
  description: string | null;
  room_name: string;
  status: string;
  host: { id: string; name: string } | null;
  course: { id: string; name: string } | null;
  recording_enabled: boolean;
  recording_status: string | null;
}

interface JoinData {
  token: string;
  ws_url: string;
  room_name: string;
  is_host: boolean;
  meeting: MeetingDetails;
}

export function MeetingRoomPage() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joinData, setJoinData] = useState<JoinData | null>(null);
  const [showPreJoin, setShowPreJoin] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'checking' | 'granted' | 'denied' | 'prompt'>('checking');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Check and request media permissions
  const requestMediaPermissions = useCallback(async () => {
    try {
      setPermissionStatus('checking');
      setPermissionError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      setLocalStream(stream);
      setPermissionStatus('granted');
      setCameraEnabled(true);
      setMicEnabled(true);
    } catch (err: any) {
      console.error('Permission error:', err);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionStatus('denied');
        setPermissionError('Camera and microphone access was denied. Please allow access in your browser settings to join the meeting.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setPermissionStatus('denied');
        setPermissionError('No camera or microphone found. Please connect a device to join with video/audio.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setPermissionStatus('denied');
        setPermissionError('Camera or microphone is already in use by another application.');
      } else {
        setPermissionStatus('denied');
        setPermissionError('Unable to access camera/microphone. Please check your browser permissions.');
      }

      // Try audio-only as fallback
      try {
        const audioOnlyStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setLocalStream(audioOnlyStream);
        setCameraEnabled(false);
        setMicEnabled(true);
        setPermissionStatus('granted');
        setPermissionError('Camera unavailable. You can join with audio only.');
      } catch {
        // Can't get any media
        setCameraEnabled(false);
        setMicEnabled(false);
      }
    }
  }, []);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [localStream]);

  // Request permissions on mount
  useEffect(() => {
    requestMediaPermissions();
  }, [requestMediaPermissions]);

  // Recording handlers
  const handleStartRecording = useCallback(async () => {
    if (!meetingId) return;
    try {
      await meetingsApi.startRecording(meetingId);
      // Refresh meeting data
      const response = await meetingsApi.join(meetingId);
      setJoinData(response as JoinData);
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  }, [meetingId]);

  const handleStopRecording = useCallback(async () => {
    if (!meetingId) return;
    try {
      await meetingsApi.stopRecording(meetingId);
      // Refresh meeting data
      const response = await meetingsApi.join(meetingId);
      setJoinData(response as JoinData);
    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
  }, [meetingId]);

  // Determine back navigation based on user role
  const getBackPath = () => {
    switch (userRole) {
      case 'admin':
        return '/admin/meetings';
      case 'teacher':
        return '/teacher/schedule';
      case 'student':
        return '/student/schedule';
      default:
        return -1;
    }
  };

  // Fetch meeting token
  useEffect(() => {
    const fetchMeetingData = async () => {
      if (!meetingId) {
        setError('Meeting ID is required');
        setLoading(false);
        return;
      }

      try {
        const response = await meetingsApi.join(meetingId);
        setJoinData(response as JoinData);
      } catch (err: any) {
        console.error('Failed to join meeting:', err);
        setError(err.message || 'Failed to join meeting. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingData();
  }, [meetingId]);

  const handleDisconnect = useCallback(() => {
    const backPath = getBackPath();
    if (typeof backPath === 'number') {
      navigate(backPath);
    } else {
      navigate(backPath);
    }
  }, [navigate, userRole]);

  if (loading) {
    return (
      <div className="h-full bg-gray-900 rounded-xl flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-400">Joining meeting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gray-900 rounded-xl flex items-center justify-center p-8">
        <div className="bg-gray-800 rounded-xl p-8 max-w-md mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Unable to Join Meeting</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={handleDisconnect}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!joinData) {
    return (
      <div className="h-full bg-gray-900 rounded-xl flex items-center justify-center">
        <p className="text-gray-400">No meeting data available</p>
      </div>
    );
  }

  // Pre-join lobby
  if (showPreJoin) {
    return (
      <PreJoinLobby
        meeting={joinData.meeting}
        userName={user?.full_name || 'Guest'}
        cameraEnabled={cameraEnabled}
        micEnabled={micEnabled}
        onCameraToggle={() => setCameraEnabled(!cameraEnabled)}
        onMicToggle={() => setMicEnabled(!micEnabled)}
        onJoin={() => {
          // Stop local preview stream before joining (LiveKit will create its own)
          if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
            setLocalStream(null);
          }
          setShowPreJoin(false);
        }}
        onCancel={handleDisconnect}
        localStream={localStream}
        permissionStatus={permissionStatus}
        permissionError={permissionError}
        onRetryPermissions={requestMediaPermissions}
      />
    );
  }

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-[calc(100vh-180px)] min-h-[500px] rounded-xl'} bg-gray-900 flex flex-col overflow-hidden`}>
      <LiveKitRoom
        serverUrl={joinData.ws_url}
        token={joinData.token}
        connect={true}
        video={cameraEnabled}
        audio={micEnabled}
        onDisconnected={handleDisconnect}
        data-lk-theme="default"
        className="flex-1 flex flex-col"
      >
        {/* Header */}
        <MeetingHeader
          meeting={joinData.meeting}
          isHost={joinData.is_host}
          onLeave={handleDisconnect}
          onToggleChat={() => setShowChat(!showChat)}
          onToggleParticipants={() => setShowParticipants(!showParticipants)}
          showChat={showChat}
          showParticipants={showParticipants}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onStartRecording={handleStartRecording}
          onStopRecording={handleStopRecording}
        />

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video grid */}
          <div className="flex-1 relative">
            <VideoConference />
          </div>

          {/* Sidebar for chat or participants */}
          {(showChat || showParticipants) && (
            <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="font-medium text-white">
                  {showChat ? 'Chat' : 'Participants'}
                </h3>
                <button
                  onClick={() => {
                    setShowChat(false);
                    setShowParticipants(false);
                  }}
                  className="p-1 text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {showChat && <Chat />}
                {showParticipants && <ParticipantsList />}
              </div>
            </div>
          )}
        </div>

        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
}

// Pre-Join Lobby Component
interface PreJoinLobbyProps {
  meeting: MeetingDetails;
  userName: string;
  cameraEnabled: boolean;
  micEnabled: boolean;
  onCameraToggle: () => void;
  onMicToggle: () => void;
  onJoin: () => void;
  onCancel: () => void;
  localStream: MediaStream | null;
  permissionStatus: 'checking' | 'granted' | 'denied' | 'prompt';
  permissionError: string | null;
  onRetryPermissions: () => void;
}

function PreJoinLobby({
  meeting,
  userName,
  cameraEnabled,
  micEnabled,
  onCameraToggle,
  onMicToggle,
  onJoin,
  onCancel,
  localStream,
  permissionStatus,
  permissionError,
  onRetryPermissions,
}: PreJoinLobbyProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && localStream && cameraEnabled) {
      videoRef.current.srcObject = localStream;
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [localStream, cameraEnabled]);

  return (
    <div className="h-full bg-gray-900 rounded-xl flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-2xl p-6 max-w-xl w-full">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-xl font-bold text-white mb-1">{meeting.title}</h1>
        {meeting.course && (
          <p className="text-gray-400 text-sm mb-4">{meeting.course.name}</p>
        )}

        {/* Permission Error */}
        {permissionStatus === 'denied' && permissionError && (
          <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-200 text-sm">{permissionError}</p>
                <button
                  onClick={onRetryPermissions}
                  className="mt-2 text-xs text-teal-400 hover:text-teal-300 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Video Preview */}
        <div className="relative bg-gray-900 rounded-xl aspect-video mb-4 overflow-hidden">
          {permissionStatus === 'checking' ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
              <p className="absolute bottom-3 left-3 text-gray-400 text-xs">
                Requesting camera access...
              </p>
            </div>
          ) : cameraEnabled && localStream ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover scale-x-[-1]"
              />
              <p className="absolute bottom-3 left-3 text-white text-xs bg-black/50 px-2 py-1 rounded">
                Camera preview
              </p>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center text-2xl font-bold text-white">
                {userName.charAt(0)}
              </div>
              <p className="absolute bottom-3 left-3 text-gray-400 text-xs">
                {!localStream ? 'Camera unavailable' : 'Camera is off'}
              </p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={onMicToggle}
            disabled={!localStream}
            className={`p-3 rounded-full transition-colors ${
              micEnabled && localStream
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-red-600 text-white hover:bg-red-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={!localStream ? 'Microphone unavailable' : micEnabled ? 'Mute' : 'Unmute'}
          >
            {micEnabled && localStream ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>
          <button
            onClick={onCameraToggle}
            disabled={!localStream || !localStream.getVideoTracks().length}
            className={`p-3 rounded-full transition-colors ${
              cameraEnabled && localStream?.getVideoTracks().length
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-red-600 text-white hover:bg-red-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={!localStream?.getVideoTracks().length ? 'Camera unavailable' : cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {cameraEnabled && localStream?.getVideoTracks().length ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>
        </div>

        {/* Join Info */}
        <div className="text-center mb-4">
          <p className="text-gray-400 text-sm">
            Joining as <span className="text-white font-medium">{userName}</span>
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onJoin}
            disabled={permissionStatus === 'checking'}
            className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm disabled:opacity-50"
          >
            {permissionStatus === 'checking' ? 'Checking permissions...' : 'Join Meeting'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Meeting Header Component
interface MeetingHeaderProps {
  meeting: MeetingDetails;
  isHost: boolean;
  onLeave: () => void;
  onToggleChat: () => void;
  onToggleParticipants: () => void;
  showChat: boolean;
  showParticipants: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
}

function MeetingHeader({
  meeting,
  isHost,
  onLeave,
  onToggleChat,
  onToggleParticipants,
  showChat,
  showParticipants,
  isFullscreen,
  onToggleFullscreen,
  onStartRecording,
  onStopRecording,
}: MeetingHeaderProps) {
  const isRecording = meeting.recording_status === 'recording';

  return (
    <div className={`h-14 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-4 ${!isFullscreen ? 'rounded-t-xl' : ''}`}>
      <div className="flex items-center gap-4">
        <h2 className="font-medium text-white truncate max-w-[200px]">{meeting.title}</h2>
        {isRecording && (
          <span className="flex items-center gap-2 px-2 py-1 bg-red-600/20 border border-red-600/40 rounded text-red-400 text-xs">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Recording
          </span>
        )}
        {isHost && (
          <span className="px-2 py-1 bg-teal-600/20 border border-teal-600/40 rounded text-teal-400 text-xs">
            Host
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {/* Recording control for host */}
        {isHost && meeting.recording_enabled && (
          <button
            onClick={isRecording ? onStopRecording : onStartRecording}
            className={`p-2 rounded-lg transition-colors ${
              isRecording
                ? 'bg-red-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            title={isRecording ? 'Stop Recording' : 'Start Recording'}
          >
            <CircleDot className="w-5 h-5" />
          </button>
        )}

        {/* Screen share indicator */}
        <button
          className="p-2 rounded-lg transition-colors text-gray-400 hover:text-white hover:bg-gray-700"
          title="Screen Share"
        >
          <Monitor className="w-5 h-5" />
        </button>

        {/* Participants */}
        <button
          onClick={onToggleParticipants}
          className={`p-2 rounded-lg transition-colors ${
            showParticipants
              ? 'bg-teal-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
          title="Participants"
        >
          <Users className="w-5 h-5" />
        </button>

        {/* Chat */}
        <button
          onClick={onToggleChat}
          className={`p-2 rounded-lg transition-colors ${
            showChat
              ? 'bg-teal-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
          title="Chat"
        >
          <MessageSquare className="w-5 h-5" />
        </button>

        {/* Fullscreen toggle */}
        <button
          onClick={onToggleFullscreen}
          className="p-2 rounded-lg transition-colors text-gray-400 hover:text-white hover:bg-gray-700"
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </button>

        {/* Leave button */}
        <button
          onClick={onLeave}
          className="ms-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <PhoneOff className="w-4 h-4" />
          Leave
        </button>
      </div>
    </div>
  );
}

// Participants List Component
function ParticipantsList() {
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone]);

  // Group tracks by participant
  const participants = tracks.reduce((acc, track) => {
    const identity = track.participant.identity;
    if (!acc[identity]) {
      acc[identity] = {
        identity,
        name: track.participant.name || identity,
        isSpeaking: track.participant.isSpeaking,
        isCameraEnabled: false,
        isMicEnabled: false,
      };
    }
    if (track.source === Track.Source.Camera) {
      acc[identity].isCameraEnabled = track.publication?.isSubscribed ?? false;
    }
    if (track.source === Track.Source.Microphone) {
      acc[identity].isMicEnabled = track.publication?.isSubscribed ?? false;
    }
    return acc;
  }, {} as Record<string, any>);

  return (
    <div className="p-4 space-y-2">
      {Object.values(participants).map((p: any) => (
        <div
          key={p.identity}
          className={`flex items-center gap-3 p-3 rounded-lg ${
            p.isSpeaking ? 'bg-teal-600/20' : 'bg-gray-700/50'
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">
            {p.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{p.name}</p>
            <div className="flex items-center gap-2 mt-1">
              {p.isCameraEnabled ? (
                <Video className="w-3 h-3 text-green-400" />
              ) : (
                <VideoOff className="w-3 h-3 text-gray-500" />
              )}
              {p.isMicEnabled ? (
                <Mic className="w-3 h-3 text-green-400" />
              ) : (
                <MicOff className="w-3 h-3 text-gray-500" />
              )}
            </div>
          </div>
        </div>
      ))}
      {Object.keys(participants).length === 0 && (
        <p className="text-gray-400 text-center py-8">No participants yet</p>
      )}
    </div>
  );
}
